import { type JSX } from 'react'
import styled from 'styled-components'
import { Row, Column, Stack } from '@shipt/design-system-layouts'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { routes } from '@shared/constants/routes'
import { getFooterIcon } from '@/features/shared/components/Footers/Footer/utils'
import {
  footerSocialLinks,
  getFooterPrivacyLinks,
} from '@/features/shared/components/Footers/constants'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { Body, Link } from '@shipt/design-system-typography'
import { useHydrated } from '@/features/shared/components/ClientOnly'
import { VDivider } from '@/features/shared/components/Dividers'
import { LogoIcon } from '@shipt/design-system-icons'
import { usePrivacyLinks } from '@/features/shared/hooks/usePrivacyLinks'

type Props = {
  sections: JSX.Element
  children?: React.ReactNode
}

export const FooterContent = ({ sections, children }: Props) => {
  const hydrated = useHydrated()
  const privacy = getFooterPrivacyLinks(usePrivacyLinks())

  return (
    <FooterContentWrapper>
      <Column paddingBottom="4xl">
        <Stack direction="column" marginTop="4xl" spacing="lg">
          <a href={routes.UNGATED_HOMEPAGE.url}>
            <Logo aria-label="Shipt logo" />
          </a>
          {sections}
        </Stack>

        <Column spacing="xl" marginBottom="xxl">
          <Row spacing="xl">
            {footerSocialLinks.map(({ name, url }) => (
              <a
                aria-label={`Follow Shipt on ${name}`}
                target="_blank"
                key={name}
                href={url}
                rel="noreferrer"
                onClick={() => {
                  trackElementClicked({ link_name: name, next_location: url })
                }}
              >
                {getFooterIcon(name)}
              </a>
            ))}
          </Row>
        </Column>
        <Body size="sm">
          &copy; {hydrated ? new Date().getFullYear() : ''} Shipt, Inc. and its services are not
          necessarily affiliated or endorsed by the retailers on this site. Shipt does not operate
          in Alaska, Wyoming, and West Virginia.
        </Body>
        {children}
        <Row wrap marginTop="lg" spacing="sm">
          {privacy.map((link, index) => (
            <Row key={link.name} align="center">
              {index !== 0 && <Line />}
              {!!link.icon && <link.icon />}
              <Link size="md" href={link.url}>
                {link.name}
              </Link>
            </Row>
          ))}
        </Row>
      </Column>
    </FooterContentWrapper>
  )
}

const FooterContentWrapper = styled(GridLayoutContainer)`
  background-color: ${({ theme }) => theme.color.background.base.brand.secondary};
`

const Line = styled(VDivider)`
  height: 1rem;
`

const Logo = styled(LogoIcon)`
  color: ${({ theme }) => theme.color.content.base.neutral.primary};
  display: block;
`
